import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft, X, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  listProductsAdmin,
  createProduct,
  updateProduct,
  deleteProduct,
  importProductsCsv,
  listCategoriesAdmin,
} from "../api";
import { PageHeader } from "./Dashboard";
import ImageUploader from "../components/ImageUploader";
import VideoUploader from "../components/VideoUploader";
import RefreshButton from "../components/RefreshButton";

const EMPTY = {
  slug: "",
  name: "",
  category: "shirts",
  color: "",
  color_hex: "#1A1A1A",
  price: 0,
  compare_at_price: null,
  fabric: "",
  description: "",
  care: "",
  sizes: ["S", "M", "L", "XL", "XXL"],
  stock: 0,
  popularity: 50,
  is_new: false,
  images: [],
  videos: [],
};

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export function ProductsList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listProductsAdmin().then(setProducts).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const onDelete = async (slug) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await deleteProduct(slug);
      toast.success("Product deleted");
      reload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-products-list">
      <PageHeader eyebrow="Catalogue" title="Products" sub={`${products.length} pieces · live in your store`} />
      <div className="p-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <RefreshButton onClick={reload} loading={loading} testid="admin-products-refresh" />
            <CsvImportButton onDone={reload} />
          </div>
          <Link
            to="/admin/products/new"
            className="bg-[#c3ba9e] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase font-medium flex items-center gap-2 hover:bg-[#FDFAF6] transition-colors"
            data-testid="admin-add-product"
          >
            <Plus size={14} /> Add Product
          </Link>
        </div>

        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">
            <span>Image</span>
            <span>Name</span>
            <span>Category</span>
            <span>Price</span>
            <span>Stock</span>
            <span></span>
          </div>
          {loading ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : products.length === 0 ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">No products yet.</p>
          ) : (
            products.map((p) => (
              <div
                key={p.slug}
                className="grid grid-cols-[80px_2fr_1fr_1fr_1fr_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center hover:bg-[#1a1a1c] transition-colors"
                data-testid={`admin-product-row-${p.slug}`}
              >
                <img src={p.images[0]} alt={p.name} className="w-14 h-14 object-cover bg-[#1f1f21]" />
                <div>
                  <p className="text-[#e9e6db] text-sm">{p.name}</p>
                  <p className="text-[11px] text-[#5e5d54]">{p.color} · {p.fabric}</p>
                </div>
                <span className="text-[12px] text-[#9a9787] capitalize">{p.category}</span>
                <span className="text-[#e9e6db] text-sm">{fmt(p.price)}</span>
                <span className={`text-sm ${p.stock < 15 ? "text-[#c79a3a]" : "text-[#9a9787]"}`}>{p.stock}</span>
                <div className="flex justify-end gap-2">
                  <Link
                    to={`/admin/products/${p.slug}/edit`}
                    className="p-2 text-[#9a9787] hover:text-[#c3ba9e]"
                    data-testid={`admin-product-edit-${p.slug}`}
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => onDelete(p.slug)}
                    className="p-2 text-[#9a9787] hover:text-[#c97070]"
                    data-testid={`admin-product-delete-${p.slug}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ProductForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    listCategoriesAdmin()
      .then((cats) => setCategories(cats.filter((c) => c.active !== false)))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (slug && slug !== "new") {
      listProductsAdmin().then((all) => {
        const p = all.find((x) => x.slug === slug);
        if (p) setData(p);
      });
    }
  }, [slug]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...data,
        price: Number(data.price),
        compare_at_price: data.compare_at_price ? Number(data.compare_at_price) : null,
        stock: Number(data.stock),
        popularity: Number(data.popularity),
        sizes: typeof data.sizes === "string" ? data.sizes.split(",").map((s) => s.trim()).filter(Boolean) : data.sizes,
        images: (Array.isArray(data.images) ? data.images : []).filter(Boolean),
        videos: (Array.isArray(data.videos) ? data.videos : []).filter(Boolean),
      };
      if (slug && slug !== "new") {
        await updateProduct(slug, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product created");
      }
      navigate("/admin/products");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sizesText = Array.isArray(data.sizes) ? data.sizes.join(", ") : data.sizes;

  return (
    <div data-testid="admin-product-form">
      <PageHeader eyebrow={slug && slug !== "new" ? "Edit" : "New"} title={data.name || "Untitled product"} />
      <div className="p-10 max-w-3xl">
        <Link to="/admin/products" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#8c8a7e] hover:text-[#c3ba9e] mb-8">
          <ArrowLeft size={14} /> Back to products
        </Link>

        <form onSubmit={submit} className="space-y-6">
          <Field label="Slug" required>
            <input value={data.slug} onChange={(e) => set("slug", e.target.value)} disabled={slug && slug !== "new"} required data-testid="admin-product-slug" className={input} />
          </Field>
          <Field label="Name" required>
            <input value={data.name} onChange={(e) => set("name", e.target.value)} required data-testid="admin-product-name" className={input} />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Category">
              <select
                value={data.category}
                onChange={(e) => set("category", e.target.value)}
                className={input}
                data-testid="admin-product-category"
              >
                {categories.length === 0 ? (
                  <>
                    <option value="shirts">Shirts</option>
                    <option value="kurtas">Short Kurtas</option>
                  </>
                ) : (
                  categories.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.display_name || c.name}
                    </option>
                  ))
                )}
              </select>
            </Field>
            <Field label="Color label">
              <input value={data.color} onChange={(e) => set("color", e.target.value)} className={input} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-5">
            <Field label="Color hex">
              <input value={data.color_hex} onChange={(e) => set("color_hex", e.target.value)} className={input} />
            </Field>
            <Field label="Price (₹)">
              <input type="number" value={data.price} onChange={(e) => set("price", e.target.value)} className={input} />
            </Field>
            <Field label="Compare-at price (₹)">
              <input type="number" value={data.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} className={input} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-5">
            <Field label="Stock">
              <input type="number" value={data.stock} onChange={(e) => set("stock", e.target.value)} className={input} />
            </Field>
            <Field label="Popularity (0–100)">
              <input type="number" value={data.popularity} onChange={(e) => set("popularity", e.target.value)} className={input} />
            </Field>
            <Field label="New arrival">
              <select value={data.is_new ? "1" : "0"} onChange={(e) => set("is_new", e.target.value === "1")} className={input}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </Field>
          </div>
          <Field label="Fabric">
            <input value={data.fabric} onChange={(e) => set("fabric", e.target.value)} className={input} />
          </Field>
          <Field label="Sizes (comma-separated)">
            <input value={sizesText} onChange={(e) => set("sizes", e.target.value)} className={input} placeholder="S, M, L, XL" />
          </Field>
          <Field label="Description">
            <textarea rows={4} value={data.description} onChange={(e) => set("description", e.target.value)} className={`${input} resize-none`} />
          </Field>
          <Field label="Care instructions">
            <textarea rows={2} value={data.care} onChange={(e) => set("care", e.target.value)} className={`${input} resize-none`} />
          </Field>
          <Field label="Product images (drag to reorder · first image is the cover)">
            <ImageUploader
              value={Array.isArray(data.images) ? data.images : []}
              onChange={(urls) => set("images", urls)}
              folder="asvasa/products"
              max={10}
              testid="product-image-uploader"
              guidelines="1200 × 1600 px (3:4 portrait) · JPG/PNG/WEBP · under 4 MB each · keep model centred"
            />
          </Field>
          <Field label="Product videos (optional · drag & drop)">
            <VideoUploader
              value={Array.isArray(data.videos) ? data.videos : []}
              onChange={(urls) => set("videos", urls)}
              folder="asvasa/videos"
              max={3}
              maxSizeMB={80}
              testid="product-video-uploader"
            />
            <p className="mt-2 text-[11px] text-[#8c8a7e] leading-relaxed">
              <span className="text-[#c3ba9e]">Recommended:</span> 1080 × 1350 px (4:5) or 9:16 portrait · 10–30 sec · MP4/MOV · under 80 MB · keep the product in frame and avoid heavy text overlays.
            </p>
          </Field>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-[#c3ba9e] text-[#0d0d0e] px-8 py-3 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#FDFAF6] transition-colors disabled:opacity-50"
              data-testid="admin-product-save"
            >
              {saving ? "Saving…" : "Save product"}
            </button>
            <Link to="/admin/products" className="border border-[#2a2a2d] text-[#9a9787] px-8 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#1f1f21]">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const input = "w-full bg-[#0d0d0e] border border-[#2a2a2d] focus:border-[#c3ba9e] outline-none px-3 py-2.5 text-[14px] text-[#e9e6db] transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mb-2 block">
        {label}
        {required && <span className="text-[#c79a3a] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}


function CsvImportButton({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await importProductsCsv(file);
      toast.success(`Imported ${res.inserted} new · updated ${res.updated} · ${res.errors.length} errors`);
      if (res.errors.length) console.warn("CSV errors:", res.errors);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV import failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="border border-[#2a2a2d] text-[#9a9787] hover:text-[#c3ba9e] hover:border-[#c3ba9e]/50 px-4 py-2.5 text-[11px] tracking-[0.28em] uppercase flex items-center gap-2 disabled:opacity-60"
        data-testid="admin-products-import-csv"
      >
        <Upload size={13} /> {busy ? "Importing…" : "Import CSV"}
      </button>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="admin-products-csv-input" />
    </>
  );
}